import React from 'react';
import SignaturePad, { signatureIsValid } from './SignaturePad';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const isBlank = (v) => v == null || (Array.isArray(v) ? v.length === 0 : typeof v === 'object' ? Object.keys(v).length === 0 : String(v).trim() === '');

export const validateFields = (fields, values) => {
  const errors = {};
  (fields || []).forEach((f) => {
    const v = values[f.label];
    if (f.required && isBlank(v)) { errors[f.label] = 'This field is required'; return; }
    if (isBlank(v)) return;
    if (f.type === 'signature' && !signatureIsValid(v)) errors[f.label] = 'Please sign fully — a quick tap or squiggle is not accepted';
    else if (f.type === 'email' && !EMAIL_RE.test(String(v))) errors[f.label] = 'Enter a valid email address';
    else if (f.type === 'number' && Number.isNaN(Number(v))) errors[f.label] = 'Enter a valid number';
  });
  return errors;
};

export const fieldId = (label) => `field-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

const inputCls = (invalid) => `w-full px-3 py-2.5 border rounded-md text-[14px] focus:outline-none focus:ring-2 ${invalid ? 'border-[#E5387B] focus:border-[#E5387B] focus:ring-[#E5387B]/15 bg-[#FFF5F7]' : 'border-[#D5D7ED] focus:border-[#0F766E] focus:ring-[#0F766E]/15'}`;

const FieldInput = ({ field: f, value, onChange, error }) => {
  const id = fieldId(f.label);
  const invalid = !!error;
  const common = { id, 'aria-invalid': invalid, 'aria-describedby': invalid ? `${id}-error` : undefined, 'data-testid': id };
  if (f.type === 'textarea') return <textarea {...common} value={value || ''} onChange={(e) => onChange(e.target.value)} className={`${inputCls(invalid)} min-h-[100px]`} />;
  if (f.type === 'dropdown') return (
    <select {...common} value={value || ''} onChange={(e) => onChange(e.target.value)} className={`${inputCls(invalid)} bg-white`}>
      <option value="">Select an option</option>
      {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  if (f.type === 'checkbox') return (
    <div className="space-y-2" id={id} data-testid={id}>
      {(f.options || []).map((o) => (
        <label key={o} className="flex items-center gap-2 text-[14px] text-[#0A1551] cursor-pointer">
          <input type="checkbox" checked={(value || []).includes(o)} onChange={(e) => { const arr = value || []; onChange(e.target.checked ? [...arr, o] : arr.filter((x) => x !== o)); }} className="w-4 h-4 accent-[#0F766E]" />
          {o}
        </label>
      ))}
    </div>
  );
  if (f.type === 'file') return <input {...common} type="file" onChange={(e) => onChange(e.target.files?.[0]?.name || '')} className={`${inputCls(invalid)} file:mr-3 file:px-3 file:py-1 file:rounded file:border-0 file:bg-[#E6F4F2] file:text-[#0F766E] file:font-semibold`} />;
  if (f.type === 'signature') return <div id={id}><SignaturePad id={id} value={value} onChange={onChange} invalid={invalid} /></div>;
  const type = f.type === 'email' ? 'email' : f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : f.type === 'phone' ? 'tel' : 'text';
  return <input {...common} type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputCls(invalid)} />;
};

export const FormFields = ({ fields, values, onChange, errors = {} }) => (
  <div className="space-y-5">
    {(fields || []).map((f, i) => {
      const id = fieldId(f.label);
      const error = errors[f.label];
      return (
        <div key={`${f.label}-${i}`} data-testid={`field-wrap-${i}`}>
          <label htmlFor={id} className="block text-[14px] font-semibold text-[#0A1551] mb-1.5">
            {f.label} {f.required && <span className="text-[#E5387B]" aria-hidden="true">*</span>}
          </label>
          <FieldInput field={f} value={values[f.label]} onChange={(v) => onChange(f.label, v)} error={error} />
          {error && <p id={`${id}-error`} role="alert" data-testid={`${id}-error`} className="mt-1.5 text-[13px] text-[#E5387B] font-medium">{error}</p>}
        </div>
      );
    })}
  </div>
);

export const ErrorSummary = ({ errors, fields, onReview }) => {
  const labels = (fields || []).map((f) => f.label).filter((l) => errors[l]);
  if (!labels.length) return null;
  return (
    <div role="alert" aria-live="assertive" data-testid="error-summary" className="mt-6 rounded-lg border border-[#E5387B]/40 bg-[#FFF5F7] p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[14px] font-bold text-[#0A1551]">{labels.length} item{labels.length > 1 ? 's' : ''} need{labels.length > 1 ? '' : 's'} your attention</p>
          <ul className="mt-1.5 space-y-0.5 text-[13px] text-[#4A517D]">
            {labels.map((l) => <li key={l}><button type="button" onClick={() => onReview(l)} className="underline decoration-dotted hover:text-[#E5387B]">{l}</button> — {errors[l]}</li>)}
          </ul>
        </div>
        <button type="button" onClick={() => onReview(labels[0])} data-testid="review-errors-button" className="shrink-0 px-3 py-1.5 rounded-md bg-[#0A1551] text-white text-[12px] font-semibold">Review errors</button>
      </div>
    </div>
  );
};

export const focusField = (label) => {
  const el = document.getElementById(fieldId(label));
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const target = el.matches('input,select,textarea') ? el : el.querySelector('input,select,textarea,canvas');
  target?.focus?.({ preventScroll: true });
};
